import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StarField from '../effects/StarField';

const SERVICES_SECTION = { pathname: '/', hash: '#services' };

const SERVICE_KEYS = [
  { icon: 'point_of_sale', key: 'acquirencia', to: SERVICES_SECTION },
  { icon: 'account_balance_wallet', key: 'treasury', to: SERVICES_SECTION },
  { icon: 'language', key: 'fx', to: SERVICES_SECTION },
  { icon: 'credit_card', key: 'card', to: SERVICES_SECTION },
  { icon: 'api', key: 'api', to: SERVICES_SECTION },
  { icon: 'verified_user', key: 'compliance', to: SERVICES_SECTION },
];

const SEGMENT_KEYS = [
  { icon: 'shopping_cart', key: 'ecommerce' },
  { icon: 'local_shipping', key: 'importers' },
  { icon: 'devices', key: 'digital' },
  { icon: 'cloud', key: 'saas' },
  { icon: 'groups', key: 'payroll' },
  { icon: 'business_center', key: 'professional' },
];

export default function Services() {
  const { t } = useTranslation();

  return (
    <section className="relative scroll-mt-28 py-24 bg-transparent overflow-hidden" id="services">
      <StarField starCount={200} speed={0.25} />
      {/* Ambient glow */}
      <div className="absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full blur-[130px] opacity-[0.05]" style={{ background: 'radial-gradient(ellipse, #D23CB3, #A84397, transparent)' }} />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.04]" style={{ background: 'radial-gradient(ellipse, #A84397, transparent)' }} />
      <div className="relative z-10 mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
        <div className="text-center mb-16">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            {t('services.sectionTitle')} <span className="gradient-text">{t('services.sectionTitleHighlight')}</span>
          </motion.h2>
          <p className="text-on-surface-variant text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            {t('services.sectionSubtitle')}
          </p>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:items-stretch lg:grid-cols-3">
          {SERVICE_KEYS.map((s, i) => (
            <motion.div
              key={s.key}
              className="h-full min-h-0 min-w-0"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
            >
              <motion.div
                className="h-full"
                whileHover={{ y: -6, boxShadow: "0 12px 40px rgba(168,67,151,0.15)" }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <Link
                  to={s.to}
                  className="flex h-full min-h-0 flex-col rounded-xl border border-outline-variant/30 bg-[#222222] p-6 transition-colors duration-300 hover:border-purple-primary/50"
                >
                  <motion.div
                    className="mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-purple-primary/30 bg-purple-primary/10"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  >
                    <span className="material-symbols-outlined text-purple-primary">{s.icon}</span>
                  </motion.div>
                  <h3 className="mb-2 text-lg font-bold font-headline text-white">{t(`services.cards.${s.key}.title`)}</h3>
                  <p className="mb-4 min-h-0 flex-1 text-sm leading-relaxed text-on-surface-variant">{t(`services.cards.${s.key}.desc`)}</p>
                  <span className="mt-auto flex items-center gap-1 text-sm font-semibold text-accent-magenta">
                    {t('services.explore')} <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                  </span>
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Target Segments */}
        <div className="mt-20">
          <motion.p
            className="text-center text-on-surface-variant text-sm uppercase tracking-[4px] font-label mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {t('services.segmentsLabel')}
          </motion.p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {SEGMENT_KEYS.map((seg, i) => (
              <motion.div
                key={seg.key}
                className="group relative flex flex-col items-center gap-3 p-5 rounded-xl bg-[#1e1e1e] border border-outline-variant/20 cursor-default overflow-hidden"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.07, duration: 0.4 }}
                whileHover={{ borderColor: "rgba(168,67,151,0.5)", y: -4 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-primary/5 to-accent-magenta/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <motion.div
                  className="w-11 h-11 rounded-lg bg-purple-primary/10 border border-purple-primary/25 flex items-center justify-center relative z-10"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                >
                  <span className="material-symbols-outlined text-purple-primary text-xl">{seg.icon}</span>
                </motion.div>
                <span className="text-xs text-center font-medium text-on-surface-variant group-hover:text-white transition-colors duration-300 relative z-10 leading-tight">
                  {t(`services.segments.${seg.key}`)}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-center justify-between p-5 sm:p-8 bg-[#222222] border border-outline-variant/30 rounded-2xl">
          <div>
            <h3 className="text-xl md:text-2xl font-bold font-headline">
              <span className="gradient-text">{t('services.trustBanner.title')}</span> {t('services.trustBanner.titleSuffix')}
            </h3>
            <p className="text-on-surface-variant text-sm mt-1">{t('services.trustBanner.subtitle')}</p>
          </div>
          <motion.a
            href="https://compliance.crosspaysolutions.app/"
            className="mt-4 md:mt-0 px-8 py-3 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline shadow-lg shadow-purple-primary/20 relative overflow-hidden group"
            target="_blank" rel="noopener noreferrer"
            whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(168,67,151,0.4)" }}
            whileTap={{ scale: 0.97 }}
          >
            <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative">{t('services.trustBanner.cta')}</span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
